import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	CommunityInFeed,
	CurrentCommunityInfo,
	FeedPageModel,
	getFeedPageInfo
} from "client/Api/Communities/communityFeed";
import {
	isNil,
	isNotNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import { HttpRequestError } from "client/Api/httpClient";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useStableImperativeCallback,
	useToggle,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	BusyIndicator,
	ContextActionBar,
	NoResults,
	PageContentTitle,
	ToolbarAction
} from "client/Components";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CommunityFeedPage.module.less";
import { never } from "client_legacy/ndaw/errors";
import {
	CommunityKind,
	CommunityMembershipStatus,
	CommunityVisibility,
	deleteCommunity,
	joinCommunity,
	leaveCommunity,
	LeaveCommunityResult,
	markCommunityAsSeen,
	registerFollowCommunity,
	requestToJoinCommunity,
	RequestToJoinCommunityResult,
	unregisterFollowCommunity
} from "client/Api/Communities/community";
import { NavigationCallback } from "client/Navigation";
import {
	CommunityEvent,
	publish,
	subscribe,
	unsubscribe
} from "./events";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { getHubNavigationForCommunity } from "./CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import classNames from "classnames";
import { CommunityFeed } from "./CommunityFeed";
import { CommunityInfo } from "./CommunityInfo";
import { SaveCommunityResult, useEditCommunityDialog } from "./EditCommunityDialog/EditCommunityDialog";
import { useCommunityDeletionConfirmationDialog } from "./CommunityDeletionConfirmationDialog";
import { useLeaveCommunityConfirmationDialog } from "./LeaveCommunityConfirmationDialog";
import { CommunityNodeId } from "../Sidebar/HubSidebar";
import { COMMUNITY_POST_COMMENT_SECTION_FRAGMENT } from "./CommunityPostPage";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { HubEvent, publish as publishHubEvent } from "../events";
import {
	CommunityJoinabilityKind,
	getCommunityJoinabilityKind,
	getJoinabilityKindAfterLeaving,
	NonMemberJoinabilityKind,
	requestToJoinResultToMembershipStatusMap,
	VisibleCommunityJoinabilityKind
} from "./common";

export type CommunityFeedPageProps = {
	id: CommunityId;
	onNavigate: NavigationCallback;
};

export const CommunityFeedPage = (props: CommunityFeedPageProps): ReactElement => {
	const {
		id,
		onNavigate
	} = props;

	const [feedPageInfo, setFeedPageInfo] = useState<FeedPageInfoLoadingState>({ kind: "loading" });
	const [isChangingMembership, setIsChangingMembership] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [leaveConfirmationDialog, openLeaveConfirmationDialog] = useLeaveCommunityConfirmationDialog();
	const [onReloadFeedPageInfo, feedPageInfoVersion] = useForceRerender();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [hasScroll, toggleHasScroll] = useToggle(true);
	const pageRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [editDialog, openEditDialog] = useEditCommunityDialog();
	const [deleteCommunityConfirmationDialog, openDeleteCommunityConfirmationDialog] = useCommunityDeletionConfirmationDialog();

	const clearCommunityNewStatusHandler = useCallback(
		async (communityId: number, signal: AbortSignal): Promise<void> => {
			try {
				await markCommunityAsSeen(
					communityId,
					signal
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}

			publishHubEvent(
				HubEvent.IsNewStatusCleared,
				{
					kind: HubDataSource.Community,
					communityId
				}
			);
		},
		[]
	);

	const stableClearCommunityNewStatusHandler: typeof clearCommunityNewStatusHandler = useStableImperativeCallback(clearCommunityNewStatusHandler);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(feedPageInfoVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				setFeedPageInfo({ kind: "loading"});

				try {
					const {
						communitiesInFeed,
						currentCommunity,
						options
					} = await getFeedPageInfo(id, signal);

					if (isNotNil(currentCommunity)) {
						stableClearCommunityNewStatusHandler(currentCommunity.id, signal);
					}

					setFeedPageInfo({
						kind: "loaded",
						communitiesInFeed,
						options,
						currentCommunity
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					if (e instanceof HttpRequestError && e.statusCode === 404) {
						setFeedPageInfo({ kind: "not-found" });
						return;
					}

					setFeedPageInfo({ kind: "error" });

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			id,
			alertManager,
			feedPageInfoVersion,
			stableClearCommunityNewStatusHandler
		]
	);

	const currentCommunity = useMemo(
		(): CommunityInFeedWithMyFeed | undefined => {
			if (feedPageInfo.kind !== "loaded") {
				return undefined;
			}

			if (isNil(feedPageInfo.currentCommunity)) {
				return {
					id: "my-feed",
					name: localize("_MyFeed")
				};
			}

			return feedPageInfo.currentCommunity;
		},
		[feedPageInfo]
	);

	const updateCommunityInLoadedFeedData = useCallback(
		(
			id: number,
			updateCallback: (community: CommunityInFeed) => CommunityInFeed
		): void => {
			setFeedPageInfo(
				(x: FeedPageInfoLoadingState): FeedPageInfoLoadingState => {
					if (x.kind !== "loaded") {
						return x;
					}

					const { communitiesInFeed } = x;

					return {
						...x,
						communitiesInFeed: communitiesInFeed.map(
							(x): CommunityInFeed => {
								if (x.id !== id) {
									return x;
								}

								return updateCallback(x);
							}
						)
					};
				}
			);
		},
		[]
	);

	const changeMembershipStatusHandler = useCallback(
		(membershipStatus: CommunityMembershipStatus, membersCount?: number): void => {
			setFeedPageInfo(
				x => {
					if (x.kind !== "loaded"
						|| !x.currentCommunity
					) {
						return x;
					}

					const {
						currentCommunity
					} = x;

					return {
						...x,
						currentCommunity: {
							...currentCommunity,
							userInfo: {
								...currentCommunity.userInfo,
								membershipStatus
							},
							membersCount: membersCount ?? currentCommunity.membersCount
						}
					};
				}
			);
		},
		[]
	);

	const joinHandler = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot join community with id: ${currentCommunity?.id}`);
			}

			setIsChangingMembership(true);

			try {
				const membersCount: number = await joinCommunity(currentCommunity.id, globalCancellation);
				changeMembershipStatusHandler(CommunityMembershipStatus.Member, membersCount);

				publish(
					CommunityEvent.JoinedCommunity,
					{
						id: currentCommunity.id,
						name: currentCommunity.name
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsChangingMembership(false);
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			changeMembershipStatusHandler
		]
	);

	const requestToJoinHandler = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot request to join community with id: ${currentCommunity?.id}`);
			}

			setIsChangingMembership(true);

			try {
				const result: RequestToJoinCommunityResult = await requestToJoinCommunity(
					currentCommunity.id,
					globalCancellation
				);

				changeMembershipStatusHandler(requestToJoinResultToMembershipStatusMap[result]);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsChangingMembership(false);
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			changeMembershipStatusHandler
		]
	);

	const followCommunity = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot join community with id: ${currentCommunity?.id}`);
			}

			try {
				await registerFollowCommunity(currentCommunity.id, globalCancellation);

				updateCommunityInLoadedFeedData(
					currentCommunity.id,
					(x: CommunityInFeed): CommunityInFeed => ({
						...x,
						isFollowing: true
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			updateCommunityInLoadedFeedData
		]
	);

	const unfollowCommunity = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot join community with id: ${currentCommunity?.id}`);
			}

			try {
				await unregisterFollowCommunity(currentCommunity.id, globalCancellation);

				updateCommunityInLoadedFeedData(
					currentCommunity.id,
					(x: CommunityInFeed): CommunityInFeed => ({
						...x,
						isFollowing: false
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			updateCommunityInLoadedFeedData
		]
	);

	const navigateToCommunityHandler = useCallback(
		(communityId: CommunityNodeId): void => {
			onNavigate(
				getHubNavigationForCommunity({
					source: HubDataSource.Community,
					communityId
				})
			);
		},
		[onNavigate]
	);

	const leaveHandler = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot leave community with id: ${currentCommunity?.id}`);
			}

			const joinabilityKindAfterLeaving: NonMemberJoinabilityKind = getJoinabilityKindAfterLeaving(currentCommunity);

			const result: TryResult<void> = await openLeaveConfirmationDialog({
				name: currentCommunity.name,
				joinabilityKindAfterLeaving,
				hasRejoinHint: true
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsChangingMembership(true);

			try {
				const leaveResult: LeaveCommunityResult = await leaveCommunity(
					currentCommunity.id,
					globalCancellation
				);

				publish(
					CommunityEvent.LeftCommunity,
					{
						communityId: currentCommunity.id
					}
				);

				if (!leaveResult.isStillVisible) {
					navigateToCommunityHandler("my-feed");
				} else if (currentCommunity.visibility === CommunityVisibility.Private) {
					onReloadFeedPageInfo();
				} else {
					changeMembershipStatusHandler(
						CommunityMembershipStatus.NotMember,
						leaveResult.membersCount
					);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsChangingMembership(false);
		},
		[
			alertManager,
			globalCancellation,
			openLeaveConfirmationDialog,
			currentCommunity,
			changeMembershipStatusHandler,
			navigateToCommunityHandler,
			onReloadFeedPageInfo
		]
	);

	const {
		canDelete,
		canEdit
 	} = useMemo(
		(): {
			canEdit: boolean;
			canDelete: boolean;
		} => {
			if (typeof currentCommunity?.id !== "number") {
				return {
					canEdit: false,
					canDelete: false
				};
			}

			const {
				canEdit,
				canDelete
			} = currentCommunity.userInfo;

			return {
				canDelete,
				canEdit
			};
		},
		[currentCommunity]
	);

	const isCustomCommunity = useMemo(
		(): boolean => {
			return typeof currentCommunity?.id === "number"
				&& currentCommunity.kind === CommunityKind.Custom;
		},
		[currentCommunity]
	);

	const joinabilityKind = useMemo(
		(): VisibleCommunityJoinabilityKind | null => {
			if (typeof currentCommunity?.id !== "number" || !isCustomCommunity) {
				return null;
			}

			return getCommunityJoinabilityKind(currentCommunity);
		},
		[currentCommunity, isCustomCommunity]
	);

	const isFollowing = useMemo(
		(): boolean => {
			if (feedPageInfo.kind !== "loaded") {
				return false;
			}

			const currentCommunity: CommunityInFeed | undefined = feedPageInfo
				.communitiesInFeed
				.find(x => x.id === id);

			return typeof currentCommunity?.id === "number"
				&& currentCommunity.isFollowing;
		},
		[feedPageInfo, id]
	);

	useEffect(
		(): (() => void) => {
			const leftToken: string = subscribe(
				CommunityEvent.LeftCommunity,
				(_, data) => {
					updateCommunityInLoadedFeedData(
						data.communityId,
						(x: CommunityInFeed): CommunityInFeed => ({
							...x,
							isMember: false
						})
					);
				}
			);

			const joinToken: string = subscribe(
				CommunityEvent.JoinedCommunity,
				(_, data) => {
					updateCommunityInLoadedFeedData(
						data.id,
						(x: CommunityInFeed): CommunityInFeed => ({
							...x,
							isMember: true,
							isFollowing: true
						})
					);
				}
			);

			return (): void => {
				unsubscribe(leftToken);
				unsubscribe(joinToken);
			};
		},
		[updateCommunityInLoadedFeedData]
	);

	const isAccessDenied = useMemo(
		(): boolean => {
			return typeof currentCommunity?.id === "number"
				&& !currentCommunity.userInfo
					.canAccess;
		},
		[currentCommunity]
	);

	const editHandler = useCallback(
		async(): Promise<void> => {
			if (typeof id !== "number") {
				throw new Error("Can not edit a community without a valid id");
			}

			const result: TryResult<SaveCommunityResult> = await openEditDialog({
				communityId: id
			});

			if (!result.wasSuccessful) {
				return;
			}

			onReloadFeedPageInfo();
		},
		[
			id,
			onReloadFeedPageInfo,
			openEditDialog
		]
	);

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			if (typeof id !== "number") {
				throw new Error("Can not delete a community without a valid id");
			}

			const result: TryResult<void> = await openDeleteCommunityConfirmationDialog();

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteCommunity(id, globalCancellation);
				alertManager.showSuccess(localize("_CommunitySuccessfullyDeleted"));
				publish(CommunityEvent.CommunityDeleted, { communityId: id });
				navigateToCommunityHandler("my-feed");
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			globalCancellation,
			openDeleteCommunityConfirmationDialog,
			navigateToCommunityHandler,
			id
		]
	);

	const membershipActions = useMemo(
		(): readonly FeedPageAction[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "leave",
					title: (c) => c.isChangingMembership
						? localize("_Leaving") + "…"
						: localize("_Joined"),
					icon: "check",
					canExecute: (c) => c.joinabilityKind === CommunityJoinabilityKind.Member,
					isDisabled: (c) => c.isChangingMembership,
					execute: () => leaveHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "join",
					title: (c) => c.isChangingMembership
						? localize("_Joining") + "…"
						: localize("_Join"),
					icon: "plus",
					canExecute: (c) => c.joinabilityKind === CommunityJoinabilityKind.CanJoin,
					isDisabled: (c) => c.isChangingMembership,
					execute: () => joinHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "request-to-join",
					title: (c) => c.isChangingMembership
						? localize("_Requesting") + "…"
						: localize("_RequestToJoin"),
					icon: "plus",
					canExecute: (c) => c.joinabilityKind === CommunityJoinabilityKind.CanRequestToJoin,
					isDisabled: (c) => c.isChangingMembership,
					execute: () => requestToJoinHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "request-pending",
					title: localize("_RequestPending"),
					icon: "clock",
					canExecute: (c) => c.joinabilityKind === CommunityJoinabilityKind.RequestPending,
					isDisabled: () => true,
					execute: () => undefined
				}
			];
		},
		[
			joinHandler,
			leaveHandler,
			requestToJoinHandler
		]
	);

	const moreActions = useMemo(
		(): readonly FeedPageAction[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "follow",
					title: localize("_Follow"),
					icon: "plus",
					canExecute: (x): boolean => x.joinabilityKind === CommunityJoinabilityKind.Member && !x.isFollowing,
					execute: () => followCommunity()
				},
				{
					kind: ActionKind.Regular,
					name: "unfollow",
					title: localize("_Following"),
					icon: "check",
					canExecute: (x): boolean => x.joinabilityKind === CommunityJoinabilityKind.Member && x.isFollowing,
					execute: () => unfollowCommunity()
				},
				{
					kind: ActionKind.Regular,
					name: "edit",
					title: localize("_Edit"),
					icon: "pen",
					canExecute: (x) => x.canEdit,
					execute: () => editHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "delete",
					title: localize("_Delete"),
					icon: "trash-can",
					canExecute: (x) => x.canDelete,
					execute: () => deleteHandler()
				}
			];
		},
		[
			followCommunity,
			unfollowCommunity,
			deleteHandler,
			editHandler
		]
	);

	const topBarActions = useMemo(
		(): readonly FeedPageAction[] => {
			return buildActionsForTopBar<FeedPageActionContext, FeedPageAction>(
				{
					defaultActions: [
						...membershipActions,
						...moreActions
					]
				},
				false,
				isResponsive,
				false
			);
		},
		[
			membershipActions,
			moreActions,
			isResponsive
		]
	);

	const scrollToTopHandler = useCallback(
		(): void => {
			pageRef.current
				?.scrollTo({
					top: 0
				});
		},
		[]
	);

	const actionContext = useMemo(
		(): FeedPageActionContext => {
			return {
				isChangingMembership,
				joinabilityKind,
				isCustomCommunity,
				isFollowing,
				canEdit,
				canDelete
			};
		},
		[
			isChangingMembership,
			joinabilityKind,
			isCustomCommunity,
			isFollowing,
			canDelete,
			canEdit
		]
	);

	const openPostHandler = useCallback(
		(postId: number, deeplink?: "comments"): void => {
			onNavigate(
				{
					...getHubNavigationForCommunity({
						source: HubDataSource.Community,
						postId,
						communityId: id
					}),
					fragment: deeplink === "comments"
						? COMMUNITY_POST_COMMENT_SECTION_FRAGMENT
						: undefined
				}
			);
		},
		[onNavigate, id]
	);

	const renderContent = (): ReactElement => {
		switch (feedPageInfo.kind) {
			case "loading":
				return <BusyIndicator />;
			case "error":
				return (
					<div className={styles.noResultContainer}>
						<NoResults
							icon="gear"
							title={localize("_NotAbleToRetrievePosts")}
							message={localize("_WeWereUnableToRetrieveYourPosts")}
							height="fill"
							onRetry={onReloadFeedPageInfo}
						/>
					</div>
				);
			case "not-found":
				return (
					<div className={styles.noResultContainer}>
						<NoResults
							icon="users"
							title={localize("_ItemNotFound")}
							message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
							height="fill"
							onRetry={() => navigateToCommunityHandler("my-feed")}
							retryText={resolveFormattedTextString({
								template: localize("_BackToName"),
								replacements: [
									{
										what: "{{Name}}",
										filler: localize("_MyFeed")
									}
								]
							})}
							trackingId="community-not-found"
						/>
					</div>
				);
			case "loaded":
				return (
					<>
						{isNotNil(currentCommunity) && (
							<div className={styles.communityInfo}>
								{currentCommunity.id === "my-feed"
									? (
										<PageContentTitle
											title={currentCommunity.name}
											actionContext={actionContext}
											actions={topBarActions}
										/>
									)
									: (
										<CommunityInfo
											model={currentCommunity}
											membershipActions={membershipActions}
											moreActions={moreActions}
											actionContext={actionContext}
											isMobile={isMobile}
										/>
									)
								}
							</div>
						)}

						{isAccessDenied
							? (
								<div className={styles.noResultContainer}>
									<NoResults
										icon="lock"
										title={localize("_PrivateCommunity")}
										message={localize("_OnlyMembersCanViewContentFromThisCommunity")}
										height="fill"
										trackingId="community-locked"
									/>
								</div>
							)
							: (
								<>
									{isNotNil(currentCommunity)
										&& currentCommunity.id !== "my-feed"
										&& (
											<h3 className={styles.sectionTitle}>
												{localize("_Posts")}
											</h3>
										)
									}

									<CommunityFeed
										id={id}
										communitiesInFeed={feedPageInfo.communitiesInFeed}
										options={feedPageInfo.options}
										onNavigateToCommunity={navigateToCommunityHandler}
										onOpenPost={openPostHandler}
										onScrollBehaviorChange={toggleHasScroll}
										onNewPost={scrollToTopHandler}
									/>
								</>
							)
						}
						<AiAssistantFabSpacing />
					</>
				);
			default:
				never(feedPageInfo);
		}
	};

	return (
		<>
			<div
				{...setTrackingId("community-feed-page")}
				className={styles.root}
			>
				{isBusy && <BusyIndicator/>}
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}
				<div
					ref={pageRef}
					className={classNames(
						styles.page,
						{
							[styles.page_noScroll]: !hasScroll
						}
					)}
				>
					<div className={styles.content}>
						{renderContent()}
					</div>
				</div>
			</div>
			{leaveConfirmationDialog}
			{editDialog}
			{deleteCommunityConfirmationDialog}
		</>
	);
};
CommunityFeedPage.displayName="CommunityFeedPage";

export type CommunityId = "my-feed" | number;

type FeedPageInfoLoadingState = {
	kind: "error" | "loading" | "not-found";
} | (
	{
		kind: "loaded";
	} & FeedPageModel
);

type CommunityInFeedWithMyFeed = CurrentCommunityInfo
	| {
		id: "my-feed";
		name: string;
	};

export type FeedPageActionContext = {
	isChangingMembership: boolean;
	joinabilityKind: CommunityJoinabilityKind | null;
	isCustomCommunity: boolean;
	isFollowing: boolean;
	canEdit: boolean;
	canDelete: boolean;
};

export type FeedPageAction = ToolbarAction<FeedPageActionContext>;
